import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop Component
 *
 * Automatically scrolls to top when route changes
 * Shows a scroll-to-top button after scrolling down
 *
 * @param {Object} props
 * @param {boolean} props.autoScroll - Automatically scroll to top on mount (default: true)
 * @param {number} props.showAfter - Show button after scrolling this many pixels (default: 300)
 * @param {string} props.className - Additional CSS classes
 */
const ScrollToTop = ({
  autoScroll = true,
  showAfter = 300,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const { pathname } = useLocation();

  // Auto scroll to top on route change
  useEffect(() => {
    if (autoScroll) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }
  }, [autoScroll, pathname]);

  // Track scroll position
  useEffect(() => {
    const toggleVisibility = () => {
      const scrolled = window.pageYOffset;
      const progress = (scrolled / (document.documentElement.scrollHeight - window.innerHeight)) * 100;

      setScrollProgress(Math.min(progress, 100));

      if (scrolled > showAfter) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);

    return () => {
      window.removeEventListener('scroll', toggleVisibility);
    };
  }, [showAfter]);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (!isVisible) {
    return null;
  }

  return (
    <button
      onClick={scrollToTop}
      className={`
        fixed bottom-20 right-4 z-40
        w-12 h-12 rounded-full
        bg-blue-600 hover:bg-blue-700
        text-white shadow-lg
        flex items-center justify-center
        transition-all duration-300
        hover:scale-110
        focus:outline-none focus:ring-4 focus:ring-blue-300
        ${className}
      `}
      aria-label="Scroll to top of page"
      title="Scroll to top"
    >
      {/* Scroll Progress Indicator */}
      <div className="absolute inset-0 rounded-full overflow-hidden">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
          <path
            d="M18 2.0845
              a 15.9155 15.9155 0 0 1 0 31.831
              a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="2"
          />
          <path
            d="M18 2.0845
              a 15.9155 15.9155 0 0 1 0 31.831
              a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeDasharray={`${scrollProgress}, 100`}
            className="transition-all duration-300"
          />
        </svg>
      </div>

      {/* Arrow Icon */}
      <svg
        className="w-6 h-6 relative z-10"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 10l7-7m0 0l7 7m-7-7v18"
        />
      </svg>
    </button>
  );
};

export default ScrollToTop;
